import { useEffect, useId, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RetryQueryAlert } from '@/components/alerts/retry-query-alert';
import { CountryRiskBadge } from '@/components/country-risk-badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ProductionPlaceDetailsFields } from '@/features/production-declaration/components/statement/production-place/production-place-details-fields';
import { ProductionPlaceMapSection } from '@/features/production-declaration/components/statement/production-place/production-place-map-section';
import {
  DEFAULT_PRODUCTION_PLACE_MAPTILER_TILE_PROVIDER_KEY,
  isProductionPlaceMapTilerTileProviderKey,
  type ProductionPlaceMapTilerTileProviderKey,
} from '@/features/production-declaration/components/statement/production-place/production-place-maptiler-providers';
import { useProductionPlaceForm } from '@/features/production-declaration/hooks/use-production-place-form';
import { useLocalizedCountryName } from '@/hooks/use-localized-country';
import { GET_COUNTRIES_KEY, GET_COUNTRY_RISKS_KEY } from '@/keys';
import { useGetCountries } from '@/services/apis/queries/country/use-get-countries';
import { useGetCountryRisks } from '@/services/apis/queries/country/use-get-country-risks';
import { useGetForestTypes } from '@/services/apis/queries/forest-type/use-get-forest-types';
import type { ProductionPlace } from '@/types/core/production-declaration/production-place';

const PRIORITY_COUNTRY_CODES = ['CN', 'IN', 'MY', 'HK'];

type ProductionPlaceDialogProps = {
  canSubmit: boolean;
  commodityOriginId: string;
  materialId: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  productionDeclarationId: string;
  productionPlace?: ProductionPlace;
  token?: string;
};

export function ProductionPlaceDialog({
  canSubmit,
  commodityOriginId,
  materialId,
  onOpenChange,
  open,
  productionDeclarationId,
  productionPlace,
  token,
}: ProductionPlaceDialogProps) {
  const { t } = useTranslation('productionDeclaration');
  const { data: forestTypes = [], isLoading: isForestTypesLoading } =
    useGetForestTypes();
  const {
    data: countries = [],
    isLoading: isCountriesLoading,
    isError: isCountriesError,
  } = useGetCountries();
  const { getLocalizedCountry } = useLocalizedCountryName(undefined, 'none');
  const countryOptions = countries
    .flatMap((country) => {
      const label = country.isoCode
        ? getLocalizedCountry(country.isoCode)
        : null;

      if (!(label && country.isoCode)) {
        return [];
      }

      const priorityIndex = PRIORITY_COUNTRY_CODES.indexOf(
        country.isoCode.trim().toUpperCase(),
      );

      return [
        {
          label,
          value: country.id.toString(),
          priority:
            priorityIndex < 0 ? PRIORITY_COUNTRY_CODES.length : priorityIndex,
        },
      ];
    })
    .sort(
      (left, right) =>
        left.priority - right.priority ||
        left.label.localeCompare(right.label, 'en'),
    );
  const mapStyleId = useId();
  const [isImportPending, setIsImportPending] = useState(false);
  const [tileProviderKey, setTileProviderKey] =
    useState<ProductionPlaceMapTilerTileProviderKey>(
      DEFAULT_PRODUCTION_PLACE_MAPTILER_TILE_PROVIDER_KEY,
    );
  const { form, isSubmitting, onSubmit } = useProductionPlaceForm({
    commodityOriginId,
    materialId,
    onOpenChange,
    open,
    productionDeclarationId,
    productionPlace,
    token,
  });
  const selectedCountryId = Number(
    useWatch({ control: form.control, name: 'countryId' }),
  );
  const {
    data: countryRisks = [],
    isError: isRiskError,
    isPending: isRiskPending,
  } = useGetCountryRisks(token === undefined && open);
  const isRecordedCountry = productionPlace?.countryId === selectedCountryId;
  const savedCountryRisk = isRecordedCountry
    ? productionPlace.countryRiskLevel
    : countryRisks.find((country) => country.id === selectedCountryId)?.risk;
  const canSubmitForm = canSubmit && form.formState.isValid;
  const forestTypeOptions = useMemo(
    () =>
      forestTypes.map((forestType) => ({
        label: forestType.label,
        value: forestType.id.toString(),
      })),
    [forestTypes],
  );
  const formFieldClassName =
    'h-12 rounded-lg border-declaration-border bg-white px-4 text-declaration-foreground shadow-none placeholder:text-declaration-muted-foreground focus-visible:border-declaration-primary focus-visible:ring-declaration-primary/15';
  const formLabelClassName =
    'font-medium text-[0.98rem] text-declaration-foreground tracking-tight';

  const handleTileProviderChange = (value: string) => {
    if (isProductionPlaceMapTilerTileProviderKey(value)) {
      setTileProviderKey(value);
    }
  };

  useEffect(() => {
    if (!open) {
      setTileProviderKey(DEFAULT_PRODUCTION_PLACE_MAPTILER_TILE_PROVIDER_KEY);
    }
  }, [open]);

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className='max-h-[94vh] overflow-y-auto rounded-[1.6rem] border border-declaration-border bg-white p-6 shadow-[0_18px_40px_rgba(17,24,39,0.12)] sm:max-w-[min(96vw,74rem)] lg:p-8'>
        <DialogHeader className='mb-5 text-left'>
          <DialogTitle className='font-semibold text-[1.85rem] text-declaration-foreground leading-none tracking-tight'>
            {t(
              productionPlace
                ? 'materialCommodityOrigins.cards.productionPlace.editModalTitle'
                : 'materialCommodityOrigins.cards.productionPlace.modalTitle',
            )}
          </DialogTitle>
          <DialogDescription className='sr-only'>
            {t(
              productionPlace
                ? 'materialCommodityOrigins.cards.productionPlace.editModalDescription'
                : 'materialCommodityOrigins.cards.productionPlace.modalDescription',
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit}>
          {isCountriesError ? (
            <RetryQueryAlert
              message={t(
                'materialCommodityOrigins.cards.productionPlace.countriesLoadError',
              )}
              queryKey={GET_COUNTRIES_KEY}
            />
          ) : null}
          <ProductionPlaceDetailsFields
            control={form.control}
            countryOptions={countryOptions}
            countryRiskBadge={
              token === undefined &&
              selectedCountryId > 0 &&
              (!isRiskError || isRecordedCountry) ? (
                isRiskPending && !isRecordedCountry ? (
                  <span className='text-muted-foreground text-xs' role='status'>
                    {t('countryRisk.loading', { ns: 'configuration' })}
                  </span>
                ) : (
                  <CountryRiskBadge
                    context={isRecordedCountry ? 'recorded' : 'current'}
                    risk={savedCountryRisk}
                  />
                )
              ) : null
            }
            isCountriesLoading={isCountriesLoading}
            disabled={isSubmitting}
            fieldClassName={formFieldClassName}
            forestTypeOptions={forestTypeOptions}
            isForestTypesLoading={isForestTypesLoading}
            labelClassName={formLabelClassName}
          />

          {token === undefined && isRiskError ? (
            <RetryQueryAlert
              message={t('countryRisk.loadError', { ns: 'configuration' })}
              queryKey={GET_COUNTRY_RISKS_KEY}
            />
          ) : null}

          <ProductionPlaceMapSection
            control={form.control}
            disabled={isSubmitting}
            formLabelClassName={formLabelClassName}
            mapStyleId={mapStyleId}
            onTileProviderChange={handleTileProviderChange}
            onImportPendingChange={setIsImportPending}
            tileProviderKey={tileProviderKey}
          />

          <DialogFooter className='mt-6 pt-0'>
            <DialogClose asChild>
              <Button
                className='rounded-xl border-declaration-border px-5 text-declaration-foreground'
                disabled={isSubmitting}
                type='button'
                variant='outline'
              >
                {t('materialCommodityOrigins.cards.productionPlace.cancel')}
              </Button>
            </DialogClose>
            <Button
              className='rounded-lg px-7 shadow-none'
              disabled={
                isSubmitting ||
                isImportPending ||
                isCountriesLoading ||
                isCountriesError ||
                isForestTypesLoading ||
                !canSubmitForm
              }
              type='submit'
              variant='declaration-primary'
            >
              {t(productionPlace ? 'save' : 'add', { ns: 'common' })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
